import { useEffect, useRef, useState } from 'react';
import { loadMfe, unloadMfe, invalidateLoaderCache } from './loader';
import { ensureZoneJs } from './zone-loader';
import { ensureLocalize } from './localize-shim';
import { ensureRxJs } from '../legacy/rxjs-loader';
import { getTracker } from '@/telemetry/tracker';
import { LegacySpinner } from '@/ui/LegacySpinner';
import type { MfeProps } from '@ey-fso-tax/mfe-loader';

const MFE_LOAD_MAX_RETRIES = 1;
const MFE_LOAD_RETRY_DELAY_MS = 1500;

async function loadMfeWithRetry(
  appId: string,
  container: HTMLElement,
  mfeProps?: Omit<MfeProps, 'appId'>,
  attempt = 0,
): Promise<unknown> {
  try {
    return await loadMfe(appId, container, { appId, ...mfeProps });
  } catch (err) {
    if (attempt < MFE_LOAD_MAX_RETRIES) {
      console.warn(
        `[MfeOutlet] "${appId}" load failed (attempt ${attempt + 1}), retrying in ${MFE_LOAD_RETRY_DELAY_MS}ms...`,
      );
      invalidateLoaderCache(appId);
      await new Promise((r) => setTimeout(r, MFE_LOAD_RETRY_DELAY_MS));
      return loadMfeWithRetry(appId, container, mfeProps, attempt + 1);
    }
    throw err;
  }
}

interface MfeOutletProps {
  appId: string;
  layout?: 'viewport' | 'fill-parent';
  /** Forwarded to MFE on every (re-)mount. */
  mfeProps?: Omit<MfeProps, 'appId'>;
  /** Hide the built-in loading spinner (caller handles loading UI externally). */
  suppressSpinner?: boolean;
  /** Called once the MFE bundle has loaded and bootstrapped. */
  onLoaded?: () => void;
  /**
   * When provided, a change in clientId triggers an in-place unload/load cycle
   * on the same container element instead of React key-based unmount/remount, which would call destroyPlatform() and
   * corrupt MF shared singletons.
   */
  clientId?: string;
}

/**
 * Track per-container state so React 18 StrictMode's double-invoke
 * (mount → cleanup → mount) doesn't tear down + re-bootstrap the MFE.
 *
 * Why this is necessary:
 *  - Angular MFEs register `customElements.define(name, ...)` during
 *    bootstrap. CustomElement registrations are global and irreversible —
 *    once `myteams-component` is defined, a second bootstrap throws
 *    `NotSupportedError: the name "X" has already been used`.
 *  - StrictMode runs effect cleanup and re-runs the effect in dev to
 *    surface bugs. A naive unload-on-cleanup destroys the moduleRef and
 *    clears the container; the immediate re-mount then fails on define().
 *
 * The fix: defer unload by a microtask + setTimeout(0). If a fresh mount
 * for the same `(appId, container)` happens within that window, cancel
 * the deferred unload — treat the cleanup as spurious. On real navigation
 * away, the timer fires and unload proceeds normally.
 */
interface ContainerState {
  loadPromise: Promise<unknown>;
  pendingUnload: number | null;
}
const state = new WeakMap<HTMLElement, ContainerState>();

/**
 * Mounts an MFE into a host `<div>`. Re-mounts when `appId` changes; unmounts
 * on unmount. The shell route definition picks `appId` from URL params and
 * forwards it here — keeps MFE-specific routing logic out of the loader.
 */
export function MfeOutlet({
  appId,
  layout: _layout = 'viewport',
  mfeProps,
  suppressSpinner,
  onLoaded,
  clientId,
}: MfeOutletProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<Error | null>(null);
  // Track the last successfully loaded appId+clientId so we can distinguish a
  // real dep change (client switch) from a StrictMode double-invoke.
  const lastLoadedRef = useRef<{ appId: string; clientId?: string } | null>(null);
  // Shows a spinner while the MFE bundle downloads and bootstraps. Legacy MFEs
  // only call `displayLoader()` AFTER their bundle has loaded and is fetching
  // data — so the longest phase (downloading manifest + JS, defining the custom
  // element) had no indication at all. Start `true` so the spinner shows the
  // moment the outlet mounts (e.g. clicking a rail item or the orchestrator
  // header button), then clear it once `load()` resolves/rejects.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    setError(null);
    setLoading(true);

    const existing = state.get(container);

    // If a deferred unload is pending (StrictMode cleanup just fired), check
    // whether the deps actually changed (real client switch) or this is a
    // spurious StrictMode double-invoke (same appId + clientId).
    if (existing?.pendingUnload !== null && existing?.pendingUnload !== undefined) {
      const depsChanged =
        lastLoadedRef.current?.appId !== appId || lastLoadedRef.current?.clientId !== clientId;

      if (!depsChanged) {
        // StrictMode false alarm: cancel deferred unload, reuse existing mount.
        clearTimeout(existing.pendingUnload);
        existing.pendingUnload = null;
        existing.loadPromise
          .then(() => {
            if (!cancelled) {
              nudgeResize();
              setLoading(false);
              onLoaded?.();
            }
          })
          .catch((e: unknown) => {
            if (!cancelled) {
              const err = e instanceof Error ? e : new Error(String(e));
              getTracker().error(err, { stage: 'mfe_load', appId });
              setError(err);
              setLoading(false);
              onLoaded?.();
            }
          });
        return () => {
          cancelled = true;
          scheduleUnload(container, appId);
        };
      }

      // Real dep change (e.g. client switch): let the pending unload fire
      // immediately so we can reload below.
      clearTimeout(existing.pendingUnload);
      existing.pendingUnload = null;
    }

    const prev = existing?.loadPromise ?? Promise.resolve();
    const loadPromise = prev
      .catch(() => undefined)
      // Angular MFEs using i18n require $localize on globalThis before bootstrap.
      .then(() => ensureLocalize())
      // Angular MFEs need Zone.js in the page before bootstrap (NG0908).
      // React host doesn't ship it; load on demand. No-op after first call.
      .then(() => ensureZoneJs())
      // Legacy MFEs call `.pipe(map(...))` on shell-provided observables.
      // RxJS operators do `source.lift(...)` — only a real RxJS Observable
      // satisfies that, so preload the UMD bundle before bootstrap.
      .then(() => ensureRxJs())
      .then(() => loadMfeWithRetry(appId, container, mfeProps))
      .then((res) => {
        // Charting MFEs (ECharts / Highcharts / Kendo) size their canvas from
        // the container's measured box at init. If they bootstrap before the
        // shell's fixed-header offset + nav-rail gutter settle, the chart is
        // sized against a stale box and renders clipped (cut-off axes/donut).
        // Nudge them to recompute by dispatching window 'resize' across a few
        // frames once mounted. Harmless for non-charting MFEs.
        if (!cancelled) {
          lastLoadedRef.current = { appId, clientId };
          nudgeResize();
          setLoading(false);
          onLoaded?.();
        }
        return res;
      });
    state.set(container, { loadPromise, pendingUnload: null });

    loadPromise.catch((e: unknown) => {
      if (!cancelled) {
        const err = e instanceof Error ? e : new Error(String(e));
        getTracker().error(err, { stage: 'mfe_load', appId });
        setError(err);
        setLoading(false);
        onLoaded?.();
      }
    });

    return () => {
      cancelled = true;
      scheduleUnload(container, appId);
    };
    // mfeProps intentionally not in deps — we don't want to remount on every
    // prop change. MFEs that need updates should subscribe via shell SDK.
    // clientId IS in deps: on client switch the effect cleanup calls unloadMfe
    // on the SAME container, then the effect body calls loadMfe on it again.
    // This avoids React key-based unmount/remount that corrupts MF shared singletons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, clientId]);

  if (error) {
    return (
      <div role="alert" style={{ padding: '1rem', color: 'var(--motif-color-text-error, #c00)' }}>
        Failed to load MFE <code>{appId}</code>: {error.message}
      </div>
    );
  }

  return (
    <>
      {loading && !suppressSpinner ? (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={`Loading ${appId}`}
          className="shell-mfe-spinner"
        >
          <LegacySpinner />
        </div>
      ) : null}
      <div ref={containerRef} className={appId.toLowerCase()} data-mfe-outlet={appId} />
    </>
  );
}

/**
 * Dispatch a window 'resize' burst across several frames. Embedded charting
 * libraries (ECharts / Highcharts / Kendo / ag-grid) listen for window resize
 * and recompute their pixel dimensions, which fixes charts that mounted clipped
 * against a not-yet-settled container box (fixed header + nav-rail gutter).
 */
function nudgeResize(): void {
  const fire = () => window.dispatchEvent(new Event('resize'));
  requestAnimationFrame(fire);
  [150, 400, 800, 1500].forEach((ms) => window.setTimeout(fire, ms));
}

function scheduleUnload(container: HTMLElement, appId: string): void {
  const entry = state.get(container);
  if (!entry) return;
  // 0ms is enough because React's StrictMode re-mount fires synchronously in
  // the same microtask as the cleanup. Real navigations are separated by at
  // least a macrotask.
  entry.pendingUnload = window.setTimeout(() => {
    entry.pendingUnload = null;
    entry.loadPromise = entry.loadPromise
      .catch(() => undefined)
      .then(() => unloadMfe(appId, container))
      .then(() => {
        state.delete(container);
      });
  }, 0);
}
